/**
 * Weekly 의 AI 글(지난주 요약 · 프로젝트별 단락 · 살펴볼 것)과 코멘트 시드.
 *
 * 진짜 글은 서버의 AI 가 쓴다(규약은 다시 정할 예정). mock 은 그 주의 Daily 와 단계 일정으로 정해진
 * 문장틀을 채워 비슷한 모양을 만든다 — 같은 시드면 같은 글이 나온다. db 를 import 하지 않고 필요한
 * 테이블을 인자로 받는다(db.ts 가 이 모듈을 쓰므로 순환이 된다).
 */
import { today } from "@/lib/dates";
import type { Staff, WeeklyComment, WeeklyStory, WeeklyStorySection } from "@/types";
import type { MockDaily } from "./dailies";
import type { MockPhase, MockProject } from "./projects";
import { SEED_TODAY } from "./seed";

const DAY = 86_400_000;

export interface StorySources {
  dailies: MockDaily[];
  projects: MockProject[];
  projectPhases: MockPhase[];
  users: Staff[];
}

const sum = (values: number[]) => values.reduce((a, b) => a + b, 0);
const round = (value: number) => Math.round(value * 100) / 100;
const shiftDay = (date: string, days: number) =>
  new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY).toISOString().slice(0, 10);

/** 그 날이 든 주의 월요일. */
export const mondayOf = (date: string) => {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay();
  return shiftDay(date, -((day + 6) % 7));
};

const WEEKDAY = ["일", "월", "화", "수", "목", "금", "토"];
const dayLabel = (date: string) => {
  const d = new Date(`${date}T00:00:00Z`);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()} (${WEEKDAY[d.getUTCDay()]})`;
};

/** 받침이 있으면 앞, 없으면 뒤 — 「은/는」「이/가」. */
function josa(word: string, withFinal: string, without: string) {
  const code = word.charCodeAt(word.length - 1);
  if (code < 0xac00 || code > 0xd7a3) return word + without;
  return word + ((code - 0xac00) % 28 ? withFinal : without);
}

const names = (list: string[]) =>
  list.length <= 2 ? list.join("과 ") : `${list.slice(0, 2).join(", ")} 외 ${list.length - 2}명`;

function planLine(hours: number, plan: number) {
  if (!plan) return "계획 없이 올린 일이 대부분이다.";
  const diff = round(hours - plan);
  if (Math.abs(diff) < 1) return `계획(${plan}시간)대로 갔다.`;
  return diff > 0
    ? `계획(${plan}시간)보다 ${diff}시간 더 들었다.`
    : `계획(${plan}시간)보다 ${-diff}시간 덜 들었다.`;
}

function nextOf(src: StorySources, projectId: string, to: string) {
  const next = src.projectPhases
    .filter((p) => p.project_id === projectId && !p.done_date && p.due_date && p.due_date > to)
    .sort((a, b) => (a.due_date! < b.due_date! ? -1 : 1))[0];
  return next
    ? `${josa(next.name, "은", "는")} ${dayLabel(next.due_date!)} 까지다. 그 준비가 이어진다.`
    : "정해진 다음 일정은 아직 없다.";
}

interface ProjectWeek {
  project_id: string;
  name: string;
  hours: number;
  plan_hours: number;
  done: number;
  members: { user_id: string; user_name: string; hours: number; descriptions: string[] }[];
}

function projectWeek(
  src: StorySources,
  projectId: string,
  dailies: MockDaily[],
  from: string,
  to: string
): ProjectWeek {
  const lines = dailies.flatMap((daily) =>
    daily.rows
      .filter((row) => row.project_id === projectId)
      .map((row) => ({ user_id: daily.user_id, row }))
  );
  const members = [...new Set(lines.map((line) => line.user_id))]
    .map((userId) => ({
      user_id: userId,
      user_name: src.users.find((u) => u.id === userId)?.name ?? userId,
      hours: round(
        sum(lines.filter((line) => line.user_id === userId).map((line) => line.row.actual_hours))
      ),
      descriptions: [
        ...new Set(
          lines
            .filter((line) => line.user_id === userId)
            .map((line) => line.row.description.trim())
            .filter(Boolean)
        ),
      ],
    }))
    .sort((a, b) => a.user_name.localeCompare(b.user_name, "ko"));
  return {
    project_id: projectId,
    name: src.projects.find((p) => p.id === projectId)?.name ?? "",
    hours: round(sum(lines.map((line) => line.row.actual_hours))),
    plan_hours: round(sum(lines.map((line) => line.row.plan_hours))),
    done: src.projectPhases.filter(
      (p) => p.project_id === projectId && p.done_date && p.done_date >= from && p.done_date <= to
    ).length,
    members,
  };
}

function sectionOf(src: StorySources, week: ProjectWeek, to: string): WeeklyStorySection {
  const work = [...new Set(week.members.flatMap((m) => m.descriptions))].slice(0, 2);
  const body = [
    `${josa(names(week.members.map((m) => m.user_name)), "이", "가")} ${week.hours}시간을 썼다.`,
    planLine(week.hours, week.plan_hours),
    work.length ? `주로 ${work.join(", ")} 일을 했다.` : "",
    week.done ? `이 기간에 단계 ${week.done}건을 마쳤다.` : "",
  ]
    .filter(Boolean)
    .join(" ");
  const diff = round(week.hours - week.plan_hours);
  return {
    project_id: week.project_id,
    topic:
      week.plan_hours && Math.abs(diff) >= 1
        ? `${week.hours}시간, 계획보다 ${Math.abs(diff)}시간 ${diff > 0 ? "더" : "덜"}`
        : `${week.hours}시간 · ${work[0] ?? "기록 없음"}`,
    body,
    next: nextOf(src, week.project_id, to),
    // 사람마다 한 일을 앞의 세 가지로 줄여 한 줄에 — 실제로는 AI 가 Daily 를 읽고 쓴다.
    members: week.members.map((m) => ({
      user_id: m.user_id,
      summary: m.descriptions.length
        ? `${m.descriptions.slice(0, 3).join(", ")}${m.descriptions.length > 3 ? " 등" : ""}`
        : "",
    })),
  };
}

/**
 * 사람마다 정리한 문장 — 그 사람이 손댄 프로젝트를 시간 많은 순으로 「어디에서 무엇을」 한 문장으로 잇는다.
 * 실제로는 AI 가 그 사람의 Daily 를 읽고 쓴다.
 */
function peopleOf(ranked: ProjectWeek[]) {
  const byUser = new Map<string, { hours: number; parts: { hours: number; text: string }[] }>();
  for (const week of ranked)
    for (const m of week.members) {
      const entry = byUser.get(m.user_id) ?? { hours: 0, parts: [] };
      entry.hours = round(entry.hours + m.hours);
      // 한 일 중 첫 가지만 — 원문의 줄바꿈 · 「— 세부」는 걷어 한 문장에 들어가게.
      const work = (m.descriptions[0] ?? "").split("\n")[0].split(" — ")[0].trim();
      if (work) entry.parts.push({ hours: m.hours, text: `${week.name}에서 ${work}` });
      byUser.set(m.user_id, entry);
    }
  return [...byUser].map(([user_id, entry]) => {
    const parts = entry.parts.sort((a, b) => b.hours - a.hours).map((part) => part.text);
    return {
      user_id,
      summary: parts.length
        ? `${parts.slice(0, 3).join(", ")}${parts.length > 3 ? " 등" : ""}을 맡아 모두 ${entry.hours}시간을 썼다.`
        : "",
    };
  });
}

/**
 * 그 주(월요일 from)를 AI 가 쓴 글. 그 주가 끝나 다음 월요일 아침에 쓴 것으로 둔다 — 아직 끝나지 않은
 * 주이거나 제출한 시간이 하나도 없으면 null.
 */
export function buildWeeklyStory(src: StorySources, from: string): WeeklyStory | null {
  const to = shiftDay(from, 6);
  const now = today() > SEED_TODAY ? today() : SEED_TODAY;
  if (to >= now) return null;
  const dailies = src.dailies.filter((d) => d.work_date >= from && d.work_date <= to);
  const total = round(sum(dailies.map((d) => d.regular_hours + d.overtime_hours)));
  if (!total) return null;
  const overtime = round(sum(dailies.map((d) => d.overtime_hours)));
  const projectIds = [
    ...new Set(dailies.flatMap((d) => d.rows.map((row) => row.project_id)).filter(Boolean)),
  ] as string[];
  const ranked = projectIds
    .map((id) => projectWeek(src, id, dailies, from, to))
    .filter((week) => week.hours > 0)
    .sort((a, b) => b.hours - a.hours);
  const people = new Set(dailies.map((d) => d.user_id)).size;
  const [first] = ranked;

  const byDay = new Map<string, number>();
  for (const d of dailies) byDay.set(d.work_date, (byDay.get(d.work_date) ?? 0) + d.overtime_hours);
  const [busyDay, busyOvertime] = [...byDay].sort((a, b) => b[1] - a[1])[0] ?? ["", 0];

  const summary = [
    `${people}명이 ${total}시간을 썼다.`,
    first
      ? `${first.name}에 가장 많은 ${first.hours}시간이 들었다. ${planLine(first.hours, first.plan_hours)}`
      : "",
    overtime
      ? `연장은 ${overtime}시간이었고, ${dayLabel(busyDay)}에 ${round(busyOvertime)}시간으로 가장 많았다.`
      : "연장은 없었다.",
  ]
    .filter(Boolean)
    .slice(0, 2)
    .join(" ");

  // 살펴볼 것 — 연장이 가장 많은 사람, 계획 없이 올린 일
  const overtimeBy = new Map<string, number>();
  for (const d of dailies)
    overtimeBy.set(d.user_id, (overtimeBy.get(d.user_id) ?? 0) + d.overtime_hours);
  const [heavyId, heavyHours] = [...overtimeBy].sort((a, b) => b[1] - a[1])[0] ?? ["", 0];
  const heavyName = src.users.find((u) => u.id === heavyId)?.name;
  const unplanned = dailies.flatMap((d) => d.rows).filter((row) => !row.plan_description).length;

  return {
    generated_at: `${shiftDay(to, 1)}T09:00:00+09:00`,
    summary,
    sections: ranked.map((week) => sectionOf(src, week, to)),
    people: peopleOf(ranked),
    watch: [
      heavyName && heavyHours >= 3
        ? `${heavyName}의 연장이 ${round(heavyHours)}시간으로 가장 많다.`
        : "",
      unplanned ? `계획 없이 올린 작업이 ${unplanned}건이다.` : "",
    ].filter(Boolean),
  };
}

/** 지난주 보고에 관리자가 단 코멘트 한 건 — 가장 시간이 많이 든 프로젝트에. */
export function seedWeeklyComments(src: StorySources): WeeklyComment[] {
  const now = today() > SEED_TODAY ? today() : SEED_TODAY;
  const lastWeek = shiftDay(mondayOf(now), -7);
  const story = buildWeeklyStory(src, lastWeek);
  const admin = src.users.find((u) => u.role === "admin");
  const top = story?.sections[0];
  if (!top || !admin) return [];
  return [
    {
      id: "weekly-comment-1",
      week_start: lastWeek,
      project_id: top.project_id,
      parent_id: null,
      author_id: admin.id,
      body: "지난주 연장이 늘었습니다. 월요일 회의에서 일정 다시 짚겠습니다.",
      created_at: `${shiftDay(lastWeek, 7)}T09:10:00+09:00`,
    },
  ];
}
